// Detail-level axes for each receptor in the sensory radar.
//
// Each top-level taste or smell axis expands into a small set of more
// concrete descriptors (e.g. Sweet → Sucrose, Caramel, Honey…). These power
// the second-level radar shown when a user drills into a vertex of the main
// sensory radar.
//
// Long-term these will come from the same sensory test pipeline that backs
// the platform's `Target` payload; for now they're a static mock so the
// drill-down UX can ship today.

export type SensoryDetail = {
  axes: string[];
  target: number[];
  current: number[];
};

export type SensoryDetailMap = Record<string, SensoryDetail>;

export const SMELL_DETAIL: SensoryDetailMap = {
  Fruity: {
    axes: ["Strawberry", "Raspberry", "Apple", "Pear", "Banana", "Peach"],
    target: [9.1, 6.4, 3.2, 2.6, 1.4, 4.8],
    current: [8.2, 5.7, 2.6, 2.1, 1.0, 4.0],
  },
  Citrus: {
    axes: ["Lemon", "Lime", "Orange", "Grapefruit", "Bergamot"],
    target: [3.2, 1.8, 2.1, 1.4, 1.2],
    current: [2.4, 1.4, 1.6, 1.0, 0.9],
  },
  Berry: {
    axes: ["Strawberry", "Raspberry", "Blueberry", "Blackberry", "Currant"],
    target: [9.2, 7.4, 3.6, 4.1, 2.8],
    current: [8.4, 6.7, 2.9, 3.5, 2.2],
  },
  Tropical: {
    axes: ["Mango", "Pineapple", "Passion", "Coconut", "Lychee"],
    target: [3.8, 2.6, 1.4, 2.8, 1.6],
    current: [3.1, 2.0, 1.0, 2.2, 1.2],
  },
  Floral: {
    axes: ["Rose", "Jasmine", "Violet", "Lavender", "Orange Bloss."],
    target: [2.4, 1.6, 1.2, 0.6, 1.8],
    current: [2.0, 1.3, 0.9, 0.4, 1.4],
  },
  Herbal: {
    axes: ["Mint", "Basil", "Thyme", "Rosemary"],
    target: [3.6, 2.4, 1.8, 1.2],
    current: [3.0, 1.8, 1.3, 0.9],
  },
  Green: {
    axes: ["Grass", "Leaf", "Cut Stem", "Cucumber"],
    target: [4.2, 5.4, 3.8, 2.4],
    current: [3.6, 4.6, 3.1, 1.9],
  },
  Vegetal: {
    axes: ["Tomato", "Pepper", "Carrot", "Potato"],
    target: [1.0, 0.8, 0.4, 0.3],
    current: [0.7, 0.6, 0.3, 0.2],
  },
  Spicy: {
    axes: ["Cinnamon", "Clove", "Nutmeg", "Pepper"],
    target: [1.2, 0.6, 0.4, 0.3],
    current: [0.9, 0.4, 0.3, 0.2],
  },
  Woody: {
    axes: ["Cedar", "Oak", "Sandalwood", "Pine"],
    target: [2.8, 2.4, 1.4, 1.0],
    current: [2.2, 1.9, 1.0, 0.7],
  },
  Smoky: {
    axes: ["Wood Smoke", "Bacon", "Mesquite", "Char"],
    target: [0.9, 0.4, 0.3, 0.2],
    current: [0.6, 0.3, 0.2, 0.1],
  },
  Roast: {
    axes: ["Coffee", "Cocoa", "Bread Crust", "Toast"],
    target: [4.8, 5.4, 3.6, 4.2],
    current: [4.0, 4.6, 2.9, 3.5],
  },
  Dairy: {
    axes: ["Milk", "Cream", "Yogurt", "Cheese"],
    target: [6.4, 7.2, 1.4, 0.8],
    current: [5.6, 6.4, 1.1, 0.5],
  },
  Buttery: {
    axes: ["Diacetyl", "Butter", "Cultured"],
    target: [5.4, 6.2, 3.4],
    current: [4.6, 5.4, 2.7],
  },
  Creamy: {
    axes: ["Milk", "Custard", "Yogurt", "Lactone"],
    target: [7.0, 6.4, 3.2, 7.8],
    current: [6.1, 5.7, 2.6, 6.9],
  },
  Caramel: {
    axes: ["Maillard", "Toffee", "Brown Sugar", "Burnt Sugar"],
    target: [4.2, 3.8, 4.6, 2.4],
    current: [3.4, 3.1, 3.8, 1.8],
  },
  Vanilla: {
    axes: ["Vanillin", "Ethyl Vanillin", "Bourbon", "Balsamic"],
    target: [6.2, 2.4, 4.8, 3.2],
    current: [5.4, 2.0, 4.0, 2.6],
  },
  Nutty: {
    axes: ["Almond", "Hazelnut", "Peanut", "Walnut"],
    target: [1.4, 1.0, 0.6, 0.4],
    current: [1.0, 0.7, 0.4, 0.3],
  },
  Fatty: {
    axes: ["Coconut", "Butterfat", "Pork", "Oxidized"],
    target: [2.8, 3.4, 0.8, 0.4],
    current: [2.2, 2.7, 0.5, 0.3],
  },
  Fermented: {
    axes: ["Yogurt", "Cheese", "Kefir", "Sourdough"],
    target: [2.4, 1.0, 0.8, 0.6],
    current: [1.8, 0.7, 0.5, 0.4],
  },
};
